import { Typography } from "@codat/orchard-ui";
import { AxiosError } from "axios";
import classNames from "classnames";
import passwordValidator from "password-validator";
import React, { useEffect, useState } from "react";
import PasswordStrengthBar from "react-password-strength-bar";
import { PasswordOptions } from "shared";

import PasswordInput from "components/PasswordInput";

import styles from "./PasswordInputWithValidation.module.css";

interface PasswordInputWithValidationProps {
  setPassword: (password: string) => void;
  isPasswordValid: (isValid: boolean) => void;
  displayErrors: boolean;
  className?: string;
  loading: boolean;
  passwordOptionsError?: AxiosError | null;
  passwordOptions?: PasswordOptions;
  labels?: {
    primary?: string;
    secondary?: string;
  };
  cleanValues?: boolean;
}

const isDefaultOptions = (passwordOptions: PasswordOptions) => {
  return (
    !passwordOptions.requireDigit &&
    !passwordOptions.requireLowercase &&
    !passwordOptions.requireNonAlphanumeric &&
    !passwordOptions.requiredUniqueChars &&
    !passwordOptions.requireUppercase
  );
};

const getPasswordRequirementDescription = (
  passwordOptions: PasswordOptions
) => {
  let description = "Your password must include: ";
  const requirements: string | string[] = [];
  if (passwordOptions) {
    if (passwordOptions.requireDigit) {
      requirements.push("one number");
    }
    if (passwordOptions.requireLowercase) {
      requirements.push("one lowercase character");
    }
    if (passwordOptions.requireUppercase) {
      requirements.push("one uppercase character");
    }
    if (passwordOptions.requiredLength) {
      requirements.push(
        `at least ${passwordOptions.requiredLength} characters`
      );
    }
    if (passwordOptions.requireNonAlphanumeric) {
      requirements.push("one special character e.g. !@#?]");
    }
  }
  if (requirements.length > 2) {
    description += requirements.join(", ").replace(/,([^,]*)$/, ", and" + "$1");
  } else {
    description += requirements.join(", ").replace(/,([^,]*)$/, " and" + "$1");
  }

  return description;
};

const PasswordInputWithSchema: React.FC<PasswordInputWithValidationProps> = ({
  setPassword,
  isPasswordValid,
  displayErrors,
  loading,
  passwordOptions,
  passwordOptionsError,
  labels,
  cleanValues,
}) => {
  const getPasswordError = (input: string): string => {
    const schema = new passwordValidator();
    if (passwordOptions) {
      schema.is().min(passwordOptions.requiredLength);
      if (passwordOptions.requireLowercase) {
        schema.has().lowercase();
      }
      if (passwordOptions.requireUppercase) {
        schema.has().uppercase();
      }
      if (passwordOptions.requireDigit) {
        schema.has().digits();
      }
      if (passwordOptions.requireNonAlphanumeric) {
        schema.has().symbols();
      }
    }

    if (passwordOptions) {
      const errors = schema.validate(input, { list: true });
      if (typeof errors !== "boolean") {
        for (const error of errors) {
          switch (error) {
            case "min":
              return `Must contain at least ${passwordOptions.requiredLength} characters`;
            case "uppercase":
              return "Must contain at least 1 uppercase letter";
            case "lowercase":
              return "Must contain at least 1 lowercase letter";
            case "digits":
              return "Must contain at least 1 number";
            case "symbols":
              return "Must contain at least 1 special character";
          }
        }
      }
    }
    return "";
  };

  return (
    <PasswordInput
      inputName="Set Password Input"
      label={labels?.primary ?? "Password"}
      id="set-password-input"
      setPassword={setPassword}
      isPasswordValid={isPasswordValid}
      displayErrors={displayErrors}
      additionalValidation={getPasswordError}
      isSkeleton={loading}
      disabled={!!passwordOptionsError}
      autoComplete="new-password"
      placeholder=""
      cleanValue={cleanValues}
    />
  );
};

type PasswordStrength = "" | "Weak" | "Okay" | "Good" | "Strong";

const passwordStrengthWords: PasswordStrength[] = [
  "",
  "Weak",
  "Okay",
  "Good",
  "Strong",
];

const PasswordInputWithValidation: React.FC<
  PasswordInputWithValidationProps
> = ({
  setPassword,
  isPasswordValid,
  displayErrors,
  className,
  loading,
  passwordOptions,
  passwordOptionsError,
  labels,
  cleanValues,
}) => {
  const [value, setValue] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [confirmPasswordError, setConfirmPasswordError] = useState("");
  const [passwordIsValid, setPasswordIsValid] = useState(false);
  const [confirmPasswordIsValid, setConfirmPasswordIsValid] = useState(false);

  const [passwordStrength, setPasswordStrength] = useState(0);

  useEffect(() => {
    if (value !== confirmPassword) {
      setConfirmPasswordError("Passwords must match");
    } else {
      setConfirmPasswordError("");
    }
  }, [value, confirmPassword, setConfirmPasswordError]);

  useEffect(() => {
    if (cleanValues) {
      setPasswordStrength(0);
      setValue("");
    }
  }, [cleanValues]);

  useEffect(() => {
    isPasswordValid(passwordIsValid && confirmPasswordIsValid);
  }, [passwordIsValid, confirmPasswordIsValid, isPasswordValid]);

  const setNewValue = (newValue: string) => {
    setValue(newValue);
    setPassword(newValue);
  };

  const shouldUseOverrideClass =
    passwordOptions &&
    value.length >= passwordOptions.requiredLength &&
    passwordStrength === 0;

  return (
    <div
      data-testid={`password-score-${passwordStrength}`}
      className={className}
    >
      <PasswordInputWithSchema
        setPassword={setNewValue}
        isPasswordValid={setPasswordIsValid}
        displayErrors={displayErrors}
        loading={loading}
        passwordOptionsError={passwordOptionsError}
        passwordOptions={passwordOptions}
        labels={labels}
        cleanValues={cleanValues}
      />
      {passwordOptions && isDefaultOptions(passwordOptions) && (
        <div className={styles.passwordStrengthContainer}>
          {value.length >= (passwordOptions?.requiredLength || 0) && (
            <Typography
              as="p"
              variant="extraSmall"
              className={classNames({
                [styles[`passwordScore-${passwordStrength}`]]: true,
              })}
            >
              {
                passwordStrengthWords[
                  passwordOptions &&
                  value.length >= passwordOptions.requiredLength &&
                  passwordStrength === 0
                    ? 1
                    : passwordStrength
                ]
              }
            </Typography>
          )}
          <PasswordStrengthBar
            className={classNames(styles.passwordStrengthBar, {
              [styles.passwordStrengthBarWeakWorkaround]:
                shouldUseOverrideClass,
            })}
            scoreWordStyle={{ display: "none" }}
            minLength={passwordOptions.requiredLength}
            barColors={["#ddd", "#EE3355", "#FFAA33", "#4499EE", "#2AA24F"]}
            password={value}
            onChangeScore={(score) => setPasswordStrength(score)}
          />
        </div>
      )}
      {passwordOptions && (
        <Typography
          className={styles.passwordRequirements}
          as="p"
          variant="extraSmall"
        >
          {getPasswordRequirementDescription(passwordOptions)}
        </Typography>
      )}
      <PasswordInput
        inputName="Confirm Password Input"
        label={labels?.secondary ?? "Confirm password"}
        setPassword={setConfirmPassword}
        isPasswordValid={setConfirmPasswordIsValid}
        displayErrors={displayErrors}
        testId="confirm-password-input"
        id="confirm-password-input"
        errorMessage={confirmPasswordError}
        placeholder=""
        autoComplete="new-password"
        isSkeleton={loading}
        cleanValue={cleanValues}
      />
    </div>
  );
};

export default PasswordInputWithValidation;
